import { z } from 'zod';

/**
 * A calendar date with no time and no zone, as `YYYY-MM-DD`.
 *
 * Schedule dates, deadlines and due dates are calendar facts: "framing starts
 * on the 12th" does not change because the server is in another zone or
 * because the clocks went forward. Arithmetic here runs in UTC so it can never
 * be perturbed by daylight saving, and the format sorts correctly as a plain
 * string, which keeps database queries simple.
 */
export type DateOnly = string & { readonly __brand?: 'DateOnly' };

const PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isDateOnly(value: unknown): value is DateOnly {
  if (typeof value !== 'string') return false;
  const match = PATTERN.exec(value);
  if (!match) return false;
  const [, y, m, d] = match;
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  // Round-trip through UTC to reject dates such as 31 February.
  const asDate = new Date(Date.UTC(year, month - 1, day));
  return (
    asDate.getUTCFullYear() === year &&
    asDate.getUTCMonth() === month - 1 &&
    asDate.getUTCDate() === day
  );
}

export const DateOnlySchema = z.string().refine(isDateOnly, {
  message: 'Expected a calendar date as YYYY-MM-DD',
});

export function dateOnly(value: string): DateOnly {
  if (!isDateOnly(value)) {
    throw new TypeError(
      `Expected a calendar date as YYYY-MM-DD, received ${JSON.stringify(value)}`,
    );
  }
  return value;
}

function toUtc(value: DateOnly): Date {
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

function fromUtc(date: Date): DateOnly {
  return date.toISOString().slice(0, 10);
}

const MS_PER_DAY = 86_400_000;

export function addDays(value: DateOnly, days: number): DateOnly {
  if (!Number.isInteger(days)) throw new TypeError(`days must be an integer, received ${days}`);
  return fromUtc(new Date(toUtc(dateOnly(value)).getTime() + days * MS_PER_DAY));
}

export function daysBetween(from: DateOnly, to: DateOnly): number {
  return Math.round((toUtc(dateOnly(to)).getTime() - toUtc(dateOnly(from)).getTime()) / MS_PER_DAY);
}

/** ISO weekday: Monday is 1, Sunday is 7. */
export function isoWeekday(value: DateOnly): number {
  const day = toUtc(dateOnly(value)).getUTCDay();
  return day === 0 ? 7 : day;
}

export function compareDateOnly(a: DateOnly, b: DateOnly): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function minDateOnly(a: DateOnly, b: DateOnly): DateOnly {
  return compareDateOnly(a, b) <= 0 ? a : b;
}

export function maxDateOnly(a: DateOnly, b: DateOnly): DateOnly {
  return compareDateOnly(a, b) >= 0 ? a : b;
}

/** The calendar date an instant falls on, in a named time zone. */
export function toDateOnly(instant: Date, timeZone: string): DateOnly {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
  return dateOnly(parts);
}

export function todayIn(timeZone: string, now: Date = new Date()): DateOnly {
  return toDateOnly(now, timeZone);
}

/** A year and month with no day, as `YYYY-MM`. Sorts correctly as a string. */
export type YearMonth = string & { readonly __brand?: 'YearMonth' };

const MONTH_PATTERN = /^(\d{4})-(\d{2})$/;

export function isYearMonth(value: unknown): value is YearMonth {
  if (typeof value !== 'string') return false;
  const match = MONTH_PATTERN.exec(value);
  if (!match) return false;
  const month = Number(match[2]);
  return month >= 1 && month <= 12;
}

export function yearMonth(value: string): YearMonth {
  if (!isYearMonth(value)) {
    throw new TypeError(`Expected a month as YYYY-MM, received ${JSON.stringify(value)}`);
  }
  return value;
}

/** The month a date falls in. */
export function monthOf(value: DateOnly): YearMonth {
  return dateOnly(value).slice(0, 7);
}

export function startOfMonth(month: YearMonth): DateOnly {
  return `${yearMonth(month)}-01`;
}

/**
 * Move a month by a number of months, in either direction.
 *
 * Kept on the month rather than on a date so there is no question of what
 * "a month after 31 January" means — a calendar grid only ever needs the
 * month, and the ambiguous case never arises.
 */
export function addMonths(month: YearMonth, months: number): YearMonth {
  if (!Number.isInteger(months)) {
    throw new TypeError(`months must be an integer, received ${months}`);
  }
  const [y, m] = yearMonth(month).split('-').map(Number) as [number, number];
  const zeroBased = y * 12 + (m - 1) + months;
  const year = Math.floor(zeroBased / 12);
  const index = zeroBased - year * 12;
  return `${String(year).padStart(4, '0')}-${String(index + 1).padStart(2, '0')}`;
}

export function endOfMonth(month: YearMonth): DateOnly {
  return addDays(startOfMonth(addMonths(month, 1)), -1);
}

/** Days the week containing `value` starts on, for a Monday or Sunday week. */
export function startOfWeek(value: DateOnly, weekStartsOn: 'monday' | 'sunday'): DateOnly {
  const weekday = isoWeekday(value);
  // isoWeekday is 1 (Monday) to 7 (Sunday).
  const back = weekStartsOn === 'monday' ? weekday - 1 : weekday % 7;
  return addDays(value, -back);
}
